import React, { useState, useContext } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { useGlobalEvent } from 'beautiful-react-hooks';
import { useMutation, useQueryClient } from 'react-query';

import FlashContext from '~/App/Components/Flash/Context';
import useStripe from '~/react-hooks/use-stripe';
import { purchaseAddOn } from '~/App/Modules/Billing/api';
import { AddOnType } from '~/types/models/addon';
import {
  CHECKOUT_COMPLETED,
  STRIPE_MODAL_PAYMENT_PAY,
  STRIPE_MODAL_PAYMENT_SUCCESS,
} from '~/generated/AnalyticsTypes';

import useAnalytics from '~/react-hooks/use-analytics';
import { StripePaymentModal } from '~/App/Components/Modals/UpgradeModalFlow/Modals/StripePaymentModal';
import { UpgradeModalViewContext } from '~/App/Services/BillingAnalytics';

const openRollUpLimitAddOnPaymentModalEventName =
  'roll-up-limit-add-on-payment-modal::open';

export const openRollUpLimitAddOnPaymentModal = () => {
  window.dispatchEvent(new Event(openRollUpLimitAddOnPaymentModalEventName));
};

export interface RollUpLimitAddOnPaymentModalProps {
  amount: number;
}

export default function RollUpLimitAddOnPaymentModal({
  amount,
}: RollUpLimitAddOnPaymentModalProps) {
  const { createFlash } = useContext(FlashContext);
  const stripe = useStripe();
  const [open, setOpen] = useState(false);
  const onOpenRollUpLimitAddOnPaymentModal = useGlobalEvent(
    openRollUpLimitAddOnPaymentModalEventName as keyof WindowEventMap
  );
  onOpenRollUpLimitAddOnPaymentModal(() => setOpen(true));
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const closePaymentModal = () => setOpen(false);

  const trackPurchaseEvent = (event: string) =>
    analytics.track(event, {
      context: 'roll_up_limit_add_on',
      price: amount,
    });

  const { mutateAsync: purchaseAddOnMutation, isLoading } = useMutation(
    purchaseAddOn,
    {
      onSuccess: () => {
        createFlash({
          id: 'add-roll-up-limit-add-on-success',
          title: 'Succes!',
          message: 'Growth lite add-on is now enabled. Redirecting...',
        });

        trackPurchaseEvent(STRIPE_MODAL_PAYMENT_SUCCESS);

        analytics.track(CHECKOUT_COMPLETED, {
          context: UpgradeModalViewContext.GrowthLiteAddon,
        });

        queryClient.invalidateQueries('app-config');
        window.location.reload();
      },
      onError: () => {
        createFlash({
          id: 'purchase-add-on-failed',
          title: 'Error',
          message:
            'Something went wrong while processing your request. Please try again.',
          options: { style: 'danger' },
        });
      },
      onSettled: () => closePaymentModal(),
    }
  );

  const handleFormSubmit = () => {
    trackPurchaseEvent(STRIPE_MODAL_PAYMENT_PAY);
    purchaseAddOnMutation({ addon: AddOnType.LimitGreetRollUp });
  };

  const handleRequestClose = () => {
    if (isLoading) return;
    closePaymentModal();
  };

  return (
    <Elements stripe={stripe}>
      <StripePaymentModal
        amount={amount}
        open={open}
        onCloseClick={handleRequestClose}
        onSubmitClick={handleFormSubmit}
        loading={isLoading}
      />
    </Elements>
  );
}
